import { Field } from "formik";

import { RepeaterButton } from "../RepeaterButton";
import {
	ResourceProjectsStyled,
	ProjectItemsStyled,
	ProjectItemStyled,
} from "./../FormInputs/ResourceProjects/styled";
import { DocumentItem } from "../DocumentItem";
import { DocumentControls } from "../DocumentControls";
import { Checkbox } from "../FormInputs/Checkbox/Checkbox";
import {
	extractResumeFileId,
	normalizeDefaultValue,
	ensureOneDefaultFile,
} from "../../utils/resumeHelpers";

// Custom component to handle different file types for resume items
const ResumeFileItem = ({
	file,
	index,
	onEdit,
	onDelete,
	isDefault,
	onDefaultChange,
}) => {
	// Use utility function to normalize isDefault value
	let isDefaultValue = normalizeDefaultValue(isDefault);

	// Create a document format that works with DocumentItem
	let documentFormat = {
		name: "Unknown file",
		id: extractResumeFileId(file),
		type: null,
		storage_path: null,
	};

	// Handle our special File wrapper object
	if (file._isFile && file._file instanceof File) {
		documentFormat = {
			name: file._file.name,
			type: file._file.type,
			id: null,
			storage_path: null,
		};
	} else if (file instanceof File) {
		// Direct File object
		documentFormat = {
			name: file.name,
			type: file.type,
			id: null,
			storage_path: null,
		};
	} else if (file && file.file_name) {
		// Backend file format
		documentFormat = {
			id: extractResumeFileId(file),
			name: file.file_name,
			storage_path: file.file_path,
			type: file.file_type,
		};
	} else if (file && file.resume_file) {
		// Resume file structure format
		documentFormat = {
			id: extractResumeFileId(file),
			name: file.resume_file.name,
			storage_path: file.resume_file.storage_path,
			type: file.resume_file.type,
		};
	} else if (file && file.name) {
		// Simple object with name
		documentFormat = {
			id: extractResumeFileId(file),
			name: file.name,
			storage_path: file.storage_path,
			type: file.type,
		};
	}

	// Use DocumentItem for all file types
	return (
		<div className="group flex items-center gap-x-2 hover:cursor-pointer">
			<Checkbox
				name={`resume_files_is_default.${index}`}
				label=""
				checked={isDefaultValue === "1"}
				onChange={(e) => {
					onDefaultChange(index, e.target.checked ? "1" : "0");
				}}
			/>
			<div className="w-full">
				<DocumentItem
					document={documentFormat}
					editable
				>
					<DocumentControls
						item={index}
						onEdit={() => onEdit(index)}
						onDelete={() => onDelete(index)}
						className="group-hover:opacity-100"
					/>
				</DocumentItem>
			</div>
		</div>
	);
};

export const Resume = ({
	resume,
	setFieldValue,
	className,
	handleAddClick,
	handleEditClick,
	handleDeleteClick,
	...props
}) => {
	// Skip items marked for removal and ensure we have an array to work with
	const prepareResumes = (inputResume) => {
		if (!inputResume) return [];

		// filter out removed items
		if (Array.isArray(inputResume)) {
			return inputResume
				.map((item, originalIndex) => {
					// Skip items that are explicitly marked for removal
					if (
						item &&
						(item.is_remove === 1 ||
							item.is_remove === "1" ||
							item._ui_removed === true)
					) {
						return null;
					}

					// File objects need special handling since they're immutable
					if (item instanceof File) {
						// We can't add properties to a File object directly
						// Instead, return an object with all needed properties
						return {
							_isFile: true,
							_file: item,
							_originalIndex: originalIndex,
						};
					}

					// Store original index in the item for reference
					return { ...item, _originalIndex: originalIndex };
				})
				.filter(Boolean); // remove null items
		}

		// if a single item, put it in an array (unless it's marked for removal)
		if (
			typeof inputResume === "object" &&
			inputResume.is_remove !== 1 &&
			inputResume.is_remove !== "1" &&
			inputResume._ui_removed !== true
		) {
			return [{ ...inputResume, _originalIndex: 0 }];
		}

		// If a single file object, put it in an array
		if (inputResume instanceof File) {
			return [
				{
					_isFile: true,
					_file: inputResume,
					_originalIndex: 0,
				},
			];
		}

		// Default to empty array
		return [];
	};

	const filteredResumes = prepareResumes(resume);

	return (
		<ResourceProjectsStyled className={className}>
			<Field name="resume_files">
				{({ field, form, meta }) => {
					const handleDefaultChange = (index, value) => {
						value = normalizeDefaultValue(value);

						// new object for is_default values
						const newIsDefault = {
							...(form.values?.resume_files_is_default || {}),
						};

						// Get all non-removed files to find actual files count
						const actualFilesIndices = filteredResumes.map(
							(file) => file._originalIndex
						);

						// if there is only one file, set it as default
						if (actualFilesIndices.length <= 1) {
							newIsDefault[index] = "1";
							setFieldValue("resume_files_is_default", newIsDefault);
							return;
						}

						// if there are multiple files, set the selected file as default
						// Set all values to "0" first if the new value is "1" (only one can be default)
						if (value === "1") {
							// Only reset the is_default for non-removed files
							actualFilesIndices.forEach((originalIndex) => {
								newIsDefault[originalIndex] = "0";
							});
						}

						// Update the value for this index
						newIsDefault[index] = value;

						// Ensure one file is set as default
						const updatedDefaults = ensureOneDefaultFile(
							newIsDefault,
							form.values?.resume_files_is_remove || {}
						);

						// update the form value
						setFieldValue("resume_files_is_default", updatedDefaults);
					};

					return (
						<div>
							<RepeaterButton
								text="Add Resume"
								handleAddClick={handleAddClick}
								testId="add-resume"
								className="!mb-2"
							/>
							<div className="mb-2">
								<span className="font-bold">Choose a default resume. </span>
								(If you don't pick one, the first will be used.)
							</div>
							<ProjectItemsStyled>
								{filteredResumes.length > 0 &&
									filteredResumes.map((resumeItem, index) => (
										<ProjectItemStyled
											key={resumeItem?.id || `resume-item-${index}`}
										>
											<ResumeFileItem
												file={resumeItem}
												index={resumeItem._originalIndex}
												onEdit={() =>
													handleEditClick(resumeItem._originalIndex)
												}
												onDelete={() =>
													handleDeleteClick(resumeItem._originalIndex)
												}
												isDefault={
													form.values.resume_files_is_default?.[
														resumeItem._originalIndex
													] ||
													resumeItem.is_default ||
													"0"
												}
												onDefaultChange={handleDefaultChange}
											/>
										</ProjectItemStyled>
									))}
							</ProjectItemsStyled>
						</div>
					);
				}}
			</Field>
		</ResourceProjectsStyled>
	);
};
